import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { visualizationStore } from "../../stores/VisualizationStore";
import { PlotlyHost } from "./PlotlyHost";
import { NetworkHost } from "./NetworkHost";
import { MoleculeHost } from "./MoleculeHost";
import { WidgetHost } from "./WidgetHost";
import { SARTableHost } from "./SARTableHost";
import { CanvasTabStrip } from "./CanvasTabStrip";
import { CanvasSourcesPane } from "./CanvasSourcesPane";
import { CanvasResizer } from "./CanvasResizer";
import { ChartFallbackPanel } from "./ChartFallbackPanel";
import { customWidgetsEnabled } from "../../lib/customWidgets";
import "../../styles/VisualizationCanvas.css";

// Shown in place of a widget iframe when the account lacks the custom-widgets
// entitlement (PLEX-4237). Prose still narrates the result; only the render is
// withheld. Default-off means this is what unentitled users see pre- and
// post-load — no flash-of-widget.
const WidgetDisabledPanel = ({ title }) => (
  <div className="widget-host widget-host--disabled" role="note">
    <div className="widget-host__disabled-title">🔒 Custom widgets are not enabled for your account</div>
    {title && <div className="widget-host__disabled-sub">{title}</div>}
  </div>
);

export const HostFor = observer(({ kind, spec, title, onRegenerate, streaming, sourceReferencesStore }) => {
  if (kind === "plotly")    return <PlotlyHost spec={spec} title={title} onRegenerate={onRegenerate} sourceReferencesStore={sourceReferencesStore} />;
  if (kind === "network")   return <NetworkHost spec={spec} title={title} onRegenerate={onRegenerate} sourceReferencesStore={sourceReferencesStore} />;
  if (kind === "molecule")  return <MoleculeHost spec={spec} title={title} onRegenerate={onRegenerate} />;
  if (kind === "widget")    return customWidgetsEnabled()
                                     ? <WidgetHost html={spec} streaming={streaming} title={title} />
                                     : <WidgetDisabledPanel title={title} />;
  if (kind === "sar")       return <SARTableHost spec={spec} sourceReferencesStore={sourceReferencesStore} />;
  return <ChartFallbackPanel title={title} error={`Unknown chart type: ${kind}`} onRegenerate={onRegenerate} />;
});

export const VisualizationCanvas = observer(({ sourceReferencesStore, onRegenerate }) => {
  // Drive the CSS var from MobX state. The resizer also writes this var directly during drag.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty('--viz-canvas-w', `${visualizationStore.canvasWidthPercent}vw`);
    return () => {
      document.documentElement.style.removeProperty('--viz-canvas-w');
    };
  }, [visualizationStore.canvasWidthPercent]);

  // Claim the canvas for the legacy surface, mirroring ProjectView's "v2"
  // claim. Previously only v2 claimed, so exclusivity relied on v2's effect
  // firing before this component's render — true in practice, but mount-order
  // dependent. Claiming from both sides makes ownership explicit; v2 still
  // outranks legacy inside claimCanvasSurface, so a legacy canvas leaking onto
  // a v2 route cannot take the canvas away from the v2 CanvasPane.
  useEffect(() => {
    visualizationStore.claimCanvasSurface("legacy");
    return () => visualizationStore.releaseCanvasSurface("legacy");
  }, []);

  // Legacy (v1) surface: yield whenever another surface (the v2 CanvasPane)
  // owns the canvas so the two can never both mount (PLEX-4227).
  if (!visualizationStore.canvasShouldShowFor("legacy")) return null;

  const active = visualizationStore.activeChart;
  const id = visualizationStore.activeChartId;
  const onClose = () => visualizationStore.setCanvasDismissed(true);

  return (
    <>
      <CanvasResizer />
      <aside
        className="visualization-canvas"
        role="region"
        aria-label="Visualization canvas"
      >
        <CanvasTabStrip onClose={onClose} onRegenerate={onRegenerate} sourceReferencesStore={sourceReferencesStore} />
        <div className="canvas-body" role="tabpanel">
          {visualizationStore.activeTab === "sources"
            ? <CanvasSourcesPane store={sourceReferencesStore} />
            : (active
                ? <HostFor kind={active.kind} spec={active.spec} title={active.title} onRegenerate={() => onRegenerate?.(id)} streaming={active.streaming} sourceReferencesStore={sourceReferencesStore} />
                : null
              )
          }
        </div>
      </aside>
    </>
  );
});

export default VisualizationCanvas;
